import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Leg, Payload, RfqLegSettings } from '@approot/models';
import {
  AdminService,
  AuthService,
  canDisplayDF,
  ConfigService,
  LegService,
  PricingExtraService,
  ProductsConfigService,
  RfqTabService,
  TradesCaptureService,
  UserStorageService
} from '@approot/services';
import { clone, Utilities } from '@approot/utilities';
import { cloneDeep } from 'lodash';
import { catchError, skip, takeWhile } from 'rxjs/operators';
import { dataToCopyModifier } from '../utilities/leg-loading-utilities';
import {
  markFieldAsPending,
  optionsChangeHandler,
  removePendingFromField,
  setFixingSourcesCutOffsOptions
} from '../utilities/leg.utilities';
import { depositTakersAndCurrenciesChanges } from '../utilities/trade-attribute-utilities';
import { LegHelperService } from './leg-helper.service';
import { LegPricingService } from './leg-pricing.service';
@Injectable({
  providedIn: 'root'
})
export class LegMiscellaneousService {
  constructor(
    private legHelperService: LegHelperService,
    private legPricingService: LegPricingService,
    private pricingExtraService: PricingExtraService,
    private legService: LegService,
    private productsConfigService: ProductsConfigService,
    private tradesCaptureService: TradesCaptureService,
    private rfqTabService: RfqTabService,
    private configService: ConfigService,
    private authService: AuthService,
    private userStorageService: UserStorageService,
    private adminService: AdminService
  ) {}

  public optimize(elementToOptimize: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const formElem = form.get(elementToOptimize);
    let product = form.value;
    product[elementToOptimize] = 'o'; // the value changes is fired before the change is bubbled up to the parents

    formElem.setErrors(null, { emitEvent: false });
    // we cant do this.form.valid as it hasn't updated by now. It will update errors form validitiy after valuechanges

    if (form.valid) {
      // if the form is valid when we set the element to being valid
      let task = {
        taskType: 'optimize'
      };

      this.legPricingService.clearPricingData(leg);

      if (this.legHelperService.requiredFixings) {
        leg.pricingData.fixings = this.legPricingService.getFixings();
      }

      // add all of the selected legs
      let strategy = this.legService.createServicePayload(
        this.pricingExtraService.getMarketDataTimeStamp,
        this.pricingExtraService.marketDataSpreadingRule,
        [{ product, attributes: leg.attributes }],
        task,
        leg.pricingData
      );

      formElem.setErrors({ optimizing: true }); // make the form element be false while we are optimizing

      this.priceOptimizer(elementToOptimize, formElem, strategy);
    }
  }

  public onCopyLegEmitted(productType: string, tradingMode: boolean): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    let product =
      this.productsConfigService.productInfo.productSpecifications.filter(x => {
        return x.productType.longName === productType; //this.leg.productType;
      });

    let dataToCopy = cloneDeep(form.value);
    dataToCopyModifier(product, form, leg, dataToCopy);

    if (product.length) {
      const productName = product[0].productType.longName
        .replace(/\s+/g, '')
        .toLowerCase();

      const extraAttributesOptions = {
        productSpec: this.legService.getProductSpec(productName),
        isVolBased: this.rfqTabService.tabData?.isVolBased,
        addDf:
          this.rfqTabService.tabData?.isVolBased &&
          canDisplayDF(product[0].productType.shortName),
        isProviderQuote: false,
        userSettings: this.configService.userSettings(),
        removeSpot:
          this.rfqTabService.tabData?.executionContext.useSpotInRfqPanel,
        addWhiteLabelAttributes: true
      };

      if (dataToCopy.spotAsk != null && dataToCopy.spotBid != null) {
        const spotAsk = Utilities.cleanFormattedNumeric(dataToCopy.spotAsk);
        const spotBid = Utilities.cleanFormattedNumeric(dataToCopy.spotBid);
        dataToCopy.spot = (spotAsk + spotBid) / 2;
      }

      this.legService.addLeg({
        productName,
        newProduct: false,
        preSetPosition: dataToCopy,
        tradeViewMode: tradingMode,
        productSpec: rfqExecutionSettings.rfqLeg
          ? this.productsConfigService.addExtraRfqAttributes(
              extraAttributesOptions
            )
          : null,
        legBeingCopied: clone(leg)
      });
    }
  }

  private priceOptimizer(
    attributeName: string,
    formElem: AbstractControl,
    strategy: Payload
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    markFieldAsPending(form, leg, attributeName); // show spinner
    this.legService.priceLeg(strategy).subscribe(
      (resp: any) => {
        removePendingFromField(form, leg, rfqExecutionSettings, attributeName); // hide spinner
        formElem.setErrors(null, { emitEvent: false });
        formElem.setValue(resp.optimum);
      },
      () => {
        removePendingFromField(form, leg, rfqExecutionSettings, attributeName);
        formElem.setErrors(null, { emitEvent: false });
      }
    );
  }

  public legDetailsChanged(): void {
    // legs data has changed wipe data regarding the previous status
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const leg = this.legHelperService.leg;
    if (rfqExecutionSettings.static) {
      return;
    }

    if (this.legHelperService.presetsLoaded) {
      leg.pricedLeg = null;
      leg.priceHistory = []; // wipe the prices that are stored.
      leg.legDescription.valueChanged = true; // the leg description should be dimmed since the leg values have changed
      leg.extraScheduleData = {}; // when there is a change in the leg, wipe the flows as it's very likely not correct anymore
    }
    this.legHelperService.onChangeInLegPriceFn();
  }

  public depositTakerSetup(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    if (form.get('depositTaker')) {
      const entities = this.tradesCaptureService.depositTakersAndCurrencies.map(
        depositTaker => depositTaker.entity
      );

      optionsChangeHandler(
        form,
        leg,
        'depositTaker',
        entities,
        leg.fromTab ? null : ''
      ); // load the deposit takers options

      this.tradesCaptureService.depositTakersAndCurrenciesSubject
        .pipe(
          takeWhile(_ => this.legHelperService.legAlive),
          skip(1)
        )
        .subscribe(depositTakersAndCurrencies => {
          // listen to changes in the treasury curve list, to update the deposit currency, when a deposit taker is selected
          depositTakersAndCurrenciesChanges(
            depositTakersAndCurrencies,
            form,
            leg
          );
        });
    }
  }

  public fixingSourcesCutOffsFetcher(form: FormGroup, leg: Leg): void {
    const crossValue = form.get('cross')?.value;
    const depositCurrencyValue = form.get('depositCurrency')?.value;
    const alternativeCurrencyValue = form.get('alternativeCurrency')?.value;
    if (!crossValue && !(depositCurrencyValue && alternativeCurrencyValue)) {
      return;
    }
    ['fixingSource', 'forcedCashSettlementFixingSource', 'cutOff'].forEach(
      (key: 'fixingSource' | 'forcedCashSettlementFixingSource' | 'cutOff') => {
        if (form.get(key)) {
          this.getFixingSourcesCutOffs(
            key,
            form,
            leg,
            this.legHelperService.rfqExecutionSettings
          );
        }
      }
    );
  }

  public getFixingSourcesCutOffs(
    attributeToSet:
      | 'fixingSource'
      | 'forcedCashSettlementFixingSource'
      | 'cutOff',
    form: FormGroup,
    leg: Leg,
    rfqLegSettings: RfqLegSettings
  ): void {
    const task =
      attributeToSet === 'fixingSource' ||
      attributeToSet === 'forcedCashSettlementFixingSource'
        ? 'getFixingSources'
        : 'getCutOffs';
    const value = this.productsConfigService.productFormatter(
      form.value,
      leg.attributes
    );
    markFieldAsPending(form, leg, attributeToSet);
    const presetsLoaded = this.legHelperService.presetsLoaded;
    this.authService
      .fxPost<string[]>(
        { product: value, task },
        'get-user-info',
        `task=${task}`
      )
      .pipe(catchError(this.legService.handleError(`${task} error`)))
      .subscribe({
        next: (val: string[]) => {
          setFixingSourcesCutOffsOptions(
            val,
            attributeToSet,
            form,
            leg,
            this.productsConfigService.cutOffTimesAndFixingSources,
            presetsLoaded
          );
          removePendingFromField(form, leg, rfqLegSettings, attributeToSet);
          this.legHelperService.onChangeInLegFn();
        },
        error: _ =>
          removePendingFromField(form, leg, rfqLegSettings, attributeToSet)
      });
  }
}
